import { RowData } from '@tanstack/react-table';
import { LegacyHeader } from '@tanstack/react-table/legacy';
import { ColumnGroupBracketEnd, ColumnGroupToggleButton } from './ColumnGroupToggle';
import { UseColumnGroupToggleReturn } from './useColumnGroupToggle';

interface ColumnGroupToggleRowProps<TData extends RowData> {
  /** Headers of the table's first header group */
  headers: LegacyHeader<TData>[];
  /** Column where the toggle button appears */
  buttonColumn: string;
  /** Last column in the group (bracket ends here) */
  lastColumn: string;
  /** State from useColumnGroupToggle */
  toggle: UseColumnGroupToggleReturn;
  /** Tooltip text when the group is visible */
  hideTooltip?: string;
  /** Tooltip text when the group is hidden */
  showTooltip?: string;
  /** Optional class names per column header cell */
  getHeaderClassName?: (columnId: string) => string;
}

/**
 * Header row hosting a column group toggle button with its bracket indicator.
 * Render as the first row inside <thead>, before the regular header rows.
 */
export const ColumnGroupToggleRow = <TData extends RowData>({
  headers,
  buttonColumn,
  lastColumn,
  toggle,
  hideTooltip,
  showTooltip,
  getHeaderClassName
}: ColumnGroupToggleRowProps<TData>) => (
  <tr className='column-toggle-row'>
    {headers.map(header => (
      <th key={`toggle-${header.id}`} className={getHeaderClassName?.(header.id)}>
        {header.id === buttonColumn && (
          <ColumnGroupToggleButton
            visible={toggle.visible}
            onToggle={toggle.toggle}
            buttonRef={toggle.buttonRef}
            bracketWidth={toggle.bracketWidth}
            hideTooltip={hideTooltip}
            showTooltip={showTooltip}
          />
        )}
        {header.id === lastColumn && (
          <ColumnGroupBracketEnd endRef={toggle.endRef} visible={toggle.visible} />
        )}
      </th>
    ))}
  </tr>
);
